import React, { useState, useEffect } from 'react';
import questions from '../data/questions';

const SOLUTION_WORD = "HF-NEXTGEN";
const SOLUTION_LENGTH = SOLUTION_WORD.length;

const getMissingSolutionLetters = () => {
  const requiredLetters = [...SOLUTION_WORD].reduce((counts, letter) => {
    counts[letter] = (counts[letter] || 0) + 1;
    return counts;
  }, {});

  Object.values(questions).forEach(({ letter }) => {
    if (letter && requiredLetters[letter]) {
      requiredLetters[letter] -= 1;
    }
  });

  return Object.entries(requiredLetters).flatMap(([letter, count]) =>
    count > 0 ? Array(count).fill(letter) : []
  );
};

const PRESELECTED_LETTERS = getMissingSolutionLetters();

function User() {
  // Lazy initial state: liest direkt beim Initialisieren aus localStorage
  const [collectedLetters] = useState(() => {
    try {
      const saved = localStorage.getItem("collectedLetters");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });
  const [solution, setSolution] = useState(() => {
    try {
      const saved = localStorage.getItem("solution");
      const parsed = saved ? JSON.parse(saved) : null;
      if (Array.isArray(parsed) && parsed.length === SOLUTION_LENGTH) {
        return parsed;
      }
    } catch {}
    return Array(SOLUTION_LENGTH).fill('');
  });
  const [usedIndexes, setUsedIndexes] = useState(() => {
    try {
      const savedIds = localStorage.getItem("usedLetterIds");
      const parsedIds = savedIds ? JSON.parse(savedIds) : null;
      if (Array.isArray(parsedIds)) {
        return parsedIds;
      }

      const saved = localStorage.getItem("usedIndexes");
      const parsed = saved ? JSON.parse(saved) : null;
      if (Array.isArray(parsed)) {
        return parsed.map((idx) => `collected-${idx}`);
      }
    } catch {}
    return [];
  });
  const [isCorrect, setIsCorrect] = useState(() => {
    try {
      const saved = localStorage.getItem("isCorrect");
      return saved ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  });

  // Immer synchron in localStorage halten
  useEffect(() => {
    localStorage.setItem("collectedLetters", JSON.stringify(collectedLetters));
  }, [collectedLetters]);

  useEffect(() => {
    localStorage.setItem("solution", JSON.stringify(solution));
    localStorage.setItem("usedLetterIds", JSON.stringify(usedIndexes));
    localStorage.removeItem("usedIndexes");
    localStorage.setItem("isCorrect", JSON.stringify(isCorrect));
  }, [solution, usedIndexes, isCorrect]);

  const availableLetters = [
    ...collectedLetters.map((letter, idx) => ({
      id: `collected-${idx}`,
      letter,
    })),
    ...PRESELECTED_LETTERS.map((letter, idx) => ({
      id: `preset-${idx}`,
      letter,
    })),
  ];

  const handleLetterClick = (letter, letterId) => {
    if (isCorrect) return;

    const nextEmpty = solution.findIndex(l => l === '');
    if (nextEmpty === -1) return;

    const newSolution = [...solution];
    newSolution[nextEmpty] = letter;
    const newUsed = [...usedIndexes, letterId];

    setSolution(newSolution);
    setUsedIndexes(newUsed);

    const audio = new Audio("forza.mp3");
    const proposed = newSolution.join('');
    if (proposed.toUpperCase() === SOLUTION_WORD) {
      setIsCorrect(true);
      audio.play();
    }
  };

  const handleReset = () => {
    setSolution(Array(SOLUTION_LENGTH).fill(''));
    setUsedIndexes([]);
    setIsCorrect(false);
    // collectedLetters behalten wir – nur die Lösung zurücksetzen
  };

  return (
    <div style={{ marginTop: '6rem',
                marginLeft: 'auto',
                marginRight: 'auto',
                maxWidth: '500px',
                width: '100%',
                boxSizing: 'border-box',
                padding: '0 1rem', }}>
      <h2 style={{ marginTop: '1rem', textAlign: 'center' }}>Gesammelte Buchstaben</h2>

      {isCorrect && (
        <div style={{
          background: 'linear-gradient(90deg, #56ab2f, #a8e063)',
          color: '#fff',
          padding: '1rem',
          borderRadius: '12px',
          textAlign: 'center',
          marginBottom: '1rem',
          fontWeight: 'bold',
          fontSize: '1.3rem',
          boxShadow: '0 2px 8px rgba(86,171,47,0.15)'
        }}>
          🎉 Glückwunsch! Du hast das richtige Wort erraten!
        </div>
      )}

      <div style={{
        display: 'flex',
                fontSize: '2rem',
                marginBottom: '2rem',
                justifyContent: 'center',
                marginTop: '3.5rem',
      }}>
        {solution.map((letter, i) => (
          <span key={i} style={{
            borderBottom: '2px solid #333',
                        minWidth: '2rem',
                        textAlign: 'center',
                        padding: '0.5rem 0',
                        transition: 'border-color 0.2s'
          }}>
            {letter}
          </span>
        ))}
      </div>

      <div style={{
        display: 'flex',
                gap: '1rem',
                fontSize: '2rem',
                flexWrap: 'wrap',
                justifyContent: 'center',
      }}>
        {availableLetters.length > 0 ? (
          availableLetters.map(({ letter, id }) => (
            <button
              key={id}
              onClick={() => handleLetterClick(letter, id)}
              disabled={usedIndexes.includes(id) || isCorrect}
              style={{
                border: 'none',
                borderRadius: '12px',
                padding: '0.7rem 1.4rem',
                background: usedIndexes.includes(id) ? '#e0e0e0' : 'linear-gradient(90deg, #6dd5ed, #2193b0)',
                color: usedIndexes.includes(id) ? '#888' : '#fff',
                boxShadow: usedIndexes.includes(id) ? 'none' : '0 2px 8px rgba(33,147,176,0.15)',
                cursor: usedIndexes.includes(id) ? 'not-allowed' : 'pointer',
                fontWeight: 'bold',
                fontSize: '1.5rem',
                transition: 'background 0.2s, box-shadow 0.2s'
              }}
            >
              {letter}
            </button>
          ))
        ) : (
          <span>Du hast noch keine Buchstaben gesammelt.</span>
        )}
      </div>

      {!isCorrect && (
        <button
          onClick={handleReset}
          style={{
            marginBottom: '1rem',
                    marginTop: '2rem',
                    padding: '0.7rem 2rem',
                    borderRadius: '20px',
                    border: 'none',
                    background: 'linear-gradient(90deg, #ff9966, #ff5e62)',
                    color: '#fff',
                    fontWeight: 'bold',
                    fontSize: '1.2rem',
                    boxShadow: '0 2px 8px rgba(255,94,98,0.15)',
                    cursor: 'pointer',
                    letterSpacing: '1px',
                    transition: 'background 0.2s, box-shadow 0.2s',
                    display: 'block',
                    marginLeft: 'auto',
                    marginRight: 'auto',
          }}
        >
          Zurücksetzen
        </button>
      )}
    </div>
  );
}

export default User;
